'use client'

import { WidgetType } from '@lastbrand/storefront-proto-registry/api/v1/enums'
import { css } from '@lastbrand/web-ui-styled-system/css'
import { useTranslation } from 'next-i18next'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { AllReviewsDS } from '@src/components/blocks/all-reviews-ds'
import { ReviewHighlightCardDS } from '@src/components/blocks/review-highlight-card-ds'
import useRegionIdToDisplayName from '@src/components/locale/hooks/useRegionIdToDisplayName'
import { PLBaseErrorBoundary } from '@src/components/pl-base-error-boundary'
import { PLTypedWidgetInterface, PLTypedWidgetProps } from '@src/components/pl-base-widget'
import { useWidgetOfType } from '@src/components/pl-base-widget/contexts/page-widget-store'
import { fetchLazyWidgetsBatch } from '@src/components/pl-base-widget/utils/fetch-lazy-widgets'
import { useLayoutContext, usePLPageMeta } from '@src/components/pl-page-context'
import { useIaImpressionRef, usePlPdpBundle, usePlPdpProduct } from '@src/components/widgets/use-ia-impression'
import { STATSIG_EXPERIMENTS_CONFIG } from '@src/config/statsig-experiments.config'
import { STATSIG_EXPERIMENTS, useStatsigExperiment } from '@src/experiments/statsig-integration'
import { useStatsigDynamicLocaleConfig } from '@src/experiments/statsig-integration/use-statsig-dynamic-locale-config.hook'
// eslint-disable-next-line import/no-named-as-default -- default export shares a name with a named export
import ProductReviewHighLight from '@src/quince-components/src/atoms/productReviewHighlight'
import reviewHighlightStyles from '@src/quince-components/src/atoms/reviewHighLightsSection/reviewHighlightsSectionV2.module.scss'
import { AllReviews } from '@src/quince-components/src/atoms/reviews/allReviews/AllReviews'
import useMounted from '@src/quince-site-common/src/hooks/useMounted'
import useReviewSystemV3Experiment from '@src/quince-site-common/src/hooks/useReviewSystemV3Experiment'
// PL-only photo-popup state manager + popups (copies of the legacy ones, with pagination). Used so
// the shared legacy popups/manager stay untouched.
import plReviewPhotoPopupStateManager from '@src/quince-site-common/src/redux/globalStateManager/PLReviewPhotoPopupStateManager'
import { scrollToRef } from '@src/quince-site-common/src/utils/scrollToRef'
import {
  getSvdProductPropertiesForTracking,
  isSvdProductForTracking,
} from '@src/telemetry/get-svd-product-properties-for-tracking'
import { getProductPropertiesForTracking } from '@src/telemetry/getProductPropertiesForTracking'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent, trackInternalEvent } from '@src/telemetry/track'
import { shouldUseDesignSystem } from '@src/utils/should-use-design-system'

import {
  adaptReviewToGallery,
  adaptReviewToHighlight,
  adaptReviewToReviewItem,
  brandNameFromTags,
} from '../shared/reviews.adapter'
import { EMPTY_PRODUCT_PROPERTIES, REVIEWS_LIST_PAGE_SIZE } from '../shared/reviews.constants'
import { useReviewsProduct, useReviewsVariant } from '../shared/reviews.context'

import type { ReviewsListWidgetData } from '@lastbrand/storefront-proto-registry/api/v1/widget'

// Internal filter identifiers/sentinels the backend echoes — not user-facing copy.
/* eslint-disable i18next/no-literal-string */
const FILTER_GROUP_FALLBACK = 'filter'
const ALL_FILTER_ID = 'all'
const ALL_FILTER_VALUE = 'All'
// The legacy `ReviewFilterTags` DEFAULT_FILTER_BY_OPTION.key (useFilterState.ts). It resets to this on
// mount when there are no filter options (e.g. the BE response omitted `filters`); we treat it as the
// aggregate `all` so it doesn't fire a bogus tag re-fetch. STRFRNT-15991.
const LEGACY_DEFAULT_FILTER_KEY = 'TAGS'
// Telemetry tokens — module-level consts (not inline literals) so they read as identifiers and stay
// clear of i18next/no-literal-string without suppression comments.
const PDP_REVIEWS_ACTION = 'pdp_reviews'
const ALL_REVIEWS_SECTION_TYPE = 'All Reviews'
// Hardcoded sort ids used in the experiment CONTROL bucket. These are the exact `sorting.value`s the
// PL BFF accepts (same ids the backend returns in REVIEWS_LIST `sorting.options`), sent as
// `sorting: { value }` on re-fetch — not user-facing copy.
const REVIEW_SORT = {
  HIGHEST_RATING: 'RATING_HIGH_TO_LOW',
  LOWEST_RATING: 'RATING_LOW_TO_HIGH',
  NEWEST: 'NEWEST_TO_OLDEST',
  OLDEST: 'OLDEST_TO_NEWEST',
}
/* eslint-enable i18next/no-literal-string */

/**
 * FULL review list with live pagination. Page-based like non-PL: changing page / sort / filter
 * re-fetches the REVIEWS_LIST widget via /widgets/fetch/v2 (reviewsList: { pagination, sorting,
 * filter }) and REPLACES the current page's items. Rendered through the legacy AllReviews so the
 * DOM/styling matches non-PL exactly.
 */
function ReviewsListFullSurface({
  widgetProps,
  reviewSystemExp,
  regionIdToDisplayName,
  rewardProgrammeLearnMoreUrl,
}: {
  widgetProps: PLTypedWidgetProps<ReviewsListWidgetData>
  reviewSystemExp: ReturnType<typeof useReviewSystemV3Experiment>
  regionIdToDisplayName: Record<number, string>
  rewardProgrammeLearnMoreUrl?: string
}) {
  const { t } = useTranslation('common')
  const pageMeta = usePLPageMeta()
  const layoutContext = useLayoutContext()
  const listData = widgetProps.data.reviewsListData
  const initialPagination = widgetProps.pagination
  const limit = initialPagination?.limit ?? REVIEWS_LIST_PAGE_SIZE
  // Filter group id the backend sends (e.g. "filter"); echoed back in the filter request.
  const filterGroupId = listData?.filters?.id ?? FILTER_GROUP_FALLBACK

  // Review sort experiment (same as non-PL PDP): the SHOW bucket surfaces the backend's data-driven
  // sort options (listData.sorting.options); CONTROL falls back to the hardcoded four. Applies to the
  // FULL surface on both mobile and desktop (AllReviews renders the sort control for each).
  const { experimentBucket } = useStatsigExperiment(STATSIG_EXPERIMENTS.PDP_REVIEWS_SORT_WEB)
  const reviewSortExperimentEnabled =
    experimentBucket === STATSIG_EXPERIMENTS_CONFIG[STATSIG_EXPERIMENTS.PDP_REVIEWS_SORT_WEB].buckets?.show

  const sortByOptions = useMemo(
    () =>
      reviewSortExperimentEnabled
        ? (listData?.sorting?.options ?? []).map(option => ({ label: option.displayName, value: option.id }))
        : [
            { label: t('highest_rating'), value: REVIEW_SORT.HIGHEST_RATING },
            { label: t('lowest_rating'), value: REVIEW_SORT.LOWEST_RATING },
            { label: t('newest'), value: REVIEW_SORT.NEWEST },
            { label: t('oldest'), value: REVIEW_SORT.OLDEST },
          ],
    [reviewSortExperimentEnabled, listData, t],
  )
  const filterOptionsBase = useMemo(() => listData?.filters?.options ?? [], [listData])

  const [items, setItems] = useState(listData?.items ?? [])
  const [page, setPage] = useState(1)
  // On review page change, scroll to the top of the reviews list — parity with non-PL
  // (product-reviews-ds/legacy fire the same `scrollToRef` effect keyed on `page`). STRFRNT-15564.
  const listTopRef = useRef<HTMLDivElement | null>(null)
  const isMounted = useMounted()
  useEffect(() => {
    // Skip the initial mount so we don't scroll on first render; only scroll on subsequent page changes.
    if (!isMounted) return
    scrollToRef(
      listTopRef,
      reviewSystemExp.showReviewSummaryAndFilterTags ? 'smooth' : 'auto',
      reviewSystemExp.showReviewSummaryAndFilterTags ? 24 : 0,
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps -- match non-PL: fire only on page change
  }, [page])
  // STRFRNT-16536: never preselect a sort, in either bucket — Control shows no option selected by
  // default, and the SHOW bucket must match that instead of adopting the backend's `selected` sort
  // flag as if the shopper had chosen it. `listData.items` already reflects the backend's own
  // default order; this state only drives the UI's "selected" indicator and what gets echoed on the
  // next explicit sort/filter/page fetch, so leaving it unset changes neither.
  const [sortById, setSortById] = useState('')
  const [activeFilterId, setActiveFilterId] = useState(
    filterOptionsBase.find(option => option.selected)?.id ?? filterOptionsBase[0]?.id ?? ALL_FILTER_ID,
  )
  // overallTotal feeds the "All" chip + totalReviewsCount; viewTotal feeds the page count for the
  // currently-applied filter (a tag filter has fewer reviews than "All").
  //
  // Source the overall count from the sibling REVIEWS_HIGHLIGHT widget's `reviewsHighlightData.count`
  // (the authoritative product review total the summary header shows) rather than the REVIEWS_LIST
  // pagination `total`, which reflects the current page/filter response and can disagree with the
  // header. Fall back to pagination total, then the loaded item count, when the highlight is absent.
  const reviewsHighlight = useWidgetOfType(WidgetType.REVIEWS_HIGHLIGHT)
  const highlightTotalCount = reviewsHighlight?.widget.data?.reviewsHighlightData?.count
  const overallTotal = highlightTotalCount ?? initialPagination?.total ?? items.length
  const [viewTotal, setViewTotal] = useState(initialPagination?.total ?? items.length)
  const [isLoading, setIsLoading] = useState(false)
  const isFetchingRef = useRef(false)

  // Publish this product's spid (present on every review item) to the shared holder so the summary's
  // write-a-review button can fetch the product. Set once; harmless if the container isn't present.
  const reviewsProductCtx = useReviewsProduct()
  useEffect(() => {
    const spid = items[0]?.productId
    if (spid) reviewsProductCtx?.publishSpid(String(spid))
  }, [items, reviewsProductCtx])

  // IA-only `pdp_reviews` impression when the full All-Reviews surface scrolls into view — mirrors the
  // non-PL product-reviews section (`entity_id: pdp_reviews`, `review_section_type: 'All Reviews'`),
  // emitted InternalAnalytics-only per the PL directive (write the IA event only). Merged onto the
  // existing `listTopRef` so the surface's single outer wrapper both drives the page-change scroll and
  // the impression (fires once — the hook's `seenRef` guards re-fires).
  // Domain product (rebuilt from the sibling PRODUCT_PURCHASE payload) — the source of the exact
  // `productName`/`productTypeName` + `productProperties` the non-PL product-reviews section emits.
  const plProduct = usePlPdpProduct()
  // Bundle page (BUNDLE_PURCHASE instead of PRODUCT_PURCHASE): the non-PL reviews section resolves
  // `product = props.bundle` and emits the BUNDLE's identity (`productId: bundle.id`,
  // `productName: bundle.productTypeName`, `productTypeName: bundle.title`) while its
  // `productProperties` come from the FIRST CHILD (`firstProduct = customBundleProducts[0]`,
  // `product-reviews-legacy.tsx:133`). Mirror both here.
  const plBundle = usePlPdpBundle()
  const productId = plBundle?.id ?? layoutContext?.pdp?.productIds?.productId
  // `review_viewed` / `review_page_navigated` carry the SAME productProperties the non-PL path builds
  // from `variants[0].svid` (first variant, not the current selection).
  const reviewPropertiesSubject = plProduct ?? plBundle?.customBundleProducts?.[0]
  const reviewProductProperties = useMemo(() => {
    if (!reviewPropertiesSubject) return EMPTY_PRODUCT_PROPERTIES
    // On an SVD (V2) bundle the first child is an `SvdProduct`, whose variants carry no legacy `options`
    // — the legacy builder throws on them. Build the same properties from the VDA model instead.
    if (isSvdProductForTracking(reviewPropertiesSubject)) {
      return getSvdProductPropertiesForTracking(reviewPropertiesSubject, {
        // Non-PL keys these events on the FIRST variant (not the current selection), so pass it directly.
        variant: reviewPropertiesSubject.variants?.[0],
      })
    }
    return getProductPropertiesForTracking(
      reviewPropertiesSubject,
      reviewPropertiesSubject.variants?.[0]?.svid?.toString(),
    )
  }, [reviewPropertiesSubject])
  const getPdpReviewsImpressionParams = useCallback(
    () => ({
      action: PDP_REVIEWS_ACTION,
      context: {
        productId,
        // Mirror non-PL exactly: `productName` is the product TYPE name, `productTypeName` the title.
        // (The bundle proto carries no productTypeName yet, so it degrades to undefined there.)
        productName: plProduct?.productTypeName ?? plBundle?.productTypeName ?? undefined,
        productTypeName: plProduct?.title ?? plBundle?.title ?? pageMeta.title,
        review_impression_count: items.length,
        review_section_type: ALL_REVIEWS_SECTION_TYPE,
      },
    }),
    [
      productId,
      plProduct?.productTypeName,
      plProduct?.title,
      plBundle?.productTypeName,
      plBundle?.title,
      pageMeta.title,
      items.length,
    ],
  )
  const pdpReviewsImpressionRef = useIaImpressionRef(getPdpReviewsImpressionParams)
  const setListTopRef = useCallback(
    (el: HTMLDivElement | null) => {
      listTopRef.current = el
      pdpReviewsImpressionRef.current = el
    },
    [pdpReviewsImpressionRef],
  )

  // `pdp_reviews` page-change re-fires happen in `fetchPage` below — AFTER the new page's reviews
  // resolve — so `review_impression_count` reflects the page just loaded, not the previous page still in
  // `items` at page-change time (the stale read). Page 1 is already reported by the scroll-into-view
  // impression above, so this seeds at 1; a reset back to page 1 from a later page still re-fires,
  // matching non-PL's page-number-change semantics.
  const lastReportedPageRef = useRef(1)
  const getPdpReviewsImpressionParamsRef = useRef(getPdpReviewsImpressionParams)
  getPdpReviewsImpressionParamsRef.current = getPdpReviewsImpressionParams

  // Keep the latest mutable values in refs so the pagination/filter/sort handlers below can have a
  // STABLE identity ([] / [fetchPage] deps). This is load-bearing: the legacy <Pagination> calls
  // `onChange(currentPosition)` inside a useEffect keyed on `[currentPosition, onChange]`. If
  // `onChange` got a new identity each render — which it would, since fetchPage closes over pageMeta
  // / layoutContext (fresh refs every render) — that effect would re-fire on every render → setState
  // → re-render → infinite loop. Stable handlers make the effect fire only on real page changes; the
  // no-op guards below absorb the mount / goTo-echo `onChange(page)` call.
  const fetchCtxRef = useRef({ pageMeta, layoutContext, widgetId: widgetProps.widgetId, limit, filterGroupId })
  fetchCtxRef.current = { pageMeta, layoutContext, widgetId: widgetProps.widgetId, limit, filterGroupId }
  const selectionRef = useRef({ page, sortById, activeFilterId, items })
  selectionRef.current = { page, sortById, activeFilterId, items }

  const fetchPage = useCallback(async (nextPage: number, nextSortId: string, nextFilterId: string) => {
    if (isFetchingRef.current) return
    isFetchingRef.current = true
    setIsLoading(true)
    try {
      const { pageMeta, layoutContext, widgetId, limit, filterGroupId } = fetchCtxRef.current
      // "all" is the aggregate (no tag) → send an empty value; a real tag → send its id.
      const filterValue = nextFilterId && nextFilterId !== 'all' ? [nextFilterId] : []
      const resolved = await fetchLazyWidgetsBatch(
        [
          {
            id: widgetId,
            type: WidgetType.REVIEWS_LIST,
            request: {
              reviewsList: {
                pagination: { pageNo: nextPage - 1, limit },
                ...(nextSortId ? { sorting: { value: nextSortId } } : {}),
                filter: { id: filterGroupId, value: filterValue },
              },
            },
          },
        ],
        pageMeta,
        layoutContext,
      )
      const next = resolved[0]?.data?.reviewsListData
      const nextItems = next?.items ?? []
      setItems(nextItems)
      const nextTotal = resolved[0]?.pagination?.total
      if (nextTotal != null) setViewTotal(nextTotal)
      if (nextPage !== lastReportedPageRef.current) {
        lastReportedPageRef.current = nextPage
        const { context } = getPdpReviewsImpressionParamsRef.current()
        trackInternalEvent(TELEMETRY_EVENT_NAMES.IS_VISIBLE, {
          action: PDP_REVIEWS_ACTION,
          value: true,
          context: { ...context, review_impression_count: nextItems.length },
        })
      }
    } catch (error) {
      console.error('Failed to load reviews page:', error)
    } finally {
      isFetchingRef.current = false
      setIsLoading(false)
    }
    // Reads all mutable inputs through fetchCtxRef so the callback identity stays stable (see the
    // comment on fetchCtxRef above — an unstable identity infinite-loops <Pagination>'s onChange effect).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSetPage = useCallback(
    (nextPage: number) => {
      // Ignore the mount / goTo-echo call <Pagination> fires for the page we're already on.
      if (nextPage === selectionRef.current.page) return
      setPage(nextPage)
      void fetchPage(nextPage, selectionRef.current.sortById, selectionRef.current.activeFilterId)
    },
    [fetchPage],
  )
  // Sort/filter changes reset to page 1 (matches non-PL).
  const handleSetSortBy = useCallback(
    (value: string) => {
      if (value === selectionRef.current.sortById) return
      setSortById(value)
      setPage(1)
      void fetchPage(1, value, selectionRef.current.activeFilterId)
    },
    [fetchPage],
  )
  const handleSetFilterBy = useCallback(
    (option: { key: string; value?: string }) => {
      // Normalize the "All" / no-tag selection to our aggregate id. When the BE response omits
      // `filters`, `filterOptions` is empty and the legacy `ReviewFilterTags` mount effect resets the
      // selection to its DEFAULT_FILTER_BY_OPTION (key `'TAGS'`, value `'All'`). Left as-is, `'TAGS'`
      // doesn't match the seeded `activeFilterId` (`'all'`), so it slips past the guard below and fires
      // a re-fetch with a bogus tag value `['TAGS']` → backend returns 0 items → the list is wiped even
      // though reviews exist. Mapping the default/"All" sentinel to `'all'` makes the mount reset a
      // no-op (guard catches it) and, if it ever fires, sends the aggregate (empty) filter. STRFRNT-15991.
      const key =
        option.value === ALL_FILTER_VALUE || option.key === LEGACY_DEFAULT_FILTER_KEY ? ALL_FILTER_ID : option.key
      if (key === selectionRef.current.activeFilterId) return
      setActiveFilterId(key)
      setPage(1)
      void fetchPage(1, selectionRef.current.sortById, key)
    },
    [fetchPage],
  )

  // Open the shared review photo popup from a list item's image (maps the current page's reviews to
  // the ProductReview shape the popup flattens — same adapter the gallery uses). The list drives its
  // OWN pagination, so it seeds the popup with the current page's reviews and no fetch loader — the
  // popup's in-page arrows navigate the clicked page only (hasMore stays false).
  const openReviewPopup = useCallback((reviewId: number, imageIndex: number) => {
    plReviewPhotoPopupStateManager.showReviewPopUp({
      productReviews: selectionRef.current.items.map(adaptReviewToGallery),
      reviewId,
      imageIndex,
    })
  }, [])

  const handleReviewsBottomVisible = useCallback(
    (review_count: number, page_number: number) => {
      trackEvent(TELEMETRY_EVENT_NAMES.REVIEW_VIEWED, {
        review_count,
        page_number,
        productProperties: reviewProductProperties,
      })
    },
    [reviewProductProperties],
  )

  // The aggregate "All" option has no count → give it the overall total (ReviewsFilter's mobile row
  // shows the selected filter's count as "{n} reviews"); map it onto value 'All' so the count chip
  // is suppressed (else "All (Undefined)").
  // MEMOIZED on purpose: ReviewFilterTags has a useEffect keyed on the filterOptions array reference
  // that resets the selection to filterOptions[0] whenever that ref changes. A fresh array each
  // render would re-fire it and snap the active chip back to "All" right after a tag is clicked.
  const filterOptions = useMemo(
    () =>
      filterOptionsBase.map(option => ({
        key: option.id,
        value: option.count == null ? ALL_FILTER_VALUE : option.id,
        count: option.count ?? overallTotal,
        displayName: option.displayName,
      })),
    [filterOptionsBase, overallTotal],
  )
  const filterBy = useMemo(
    () =>
      filterOptions.find(option => option.key === activeFilterId) ??
      filterOptions[0] ?? { key: ALL_FILTER_ID, value: ALL_FILTER_VALUE, displayName: ALL_FILTER_VALUE },
    [filterOptions, activeFilterId],
  )

  const reviews = useMemo(
    () =>
      items.map((review, index) =>
        adaptReviewToReviewItem(review, index, regionIdToDisplayName, rewardProgrammeLearnMoreUrl),
      ),
    [items, regionIdToDisplayName, rewardProgrammeLearnMoreUrl],
  )
  const totalPages = Math.max(1, Math.ceil(viewTotal / limit))

  if (shouldUseDesignSystem()) {
    return (
      <div ref={setListTopRef}>
        <AllReviewsDS
          reviews={reviews}
          paginationProps={{ totalPages, page, onChange: handleSetPage, productProperties: reviewProductProperties }}
          filterProps={{
            sortByOptions,
            sortBy: sortById,
            setSortBy: handleSetSortBy,
            onSearchReview: () => undefined,
            setHasPhoto: () => undefined,
            hasPhoto: false,
            filterOptions,
            setFilterBy: handleSetFilterBy,
            filterBy,
            setPage: handleSetPage,
          }}
          showReviewsLoader={isLoading}
          onReviewImageClick={openReviewPopup}
          // Empty array (PL has no product variants here): the shared sort Select reads `variants[0]?.productId`
          // for analytics — the optional chain guards the element, not the array, so an undefined `variants`
          // crashes on sort click. `[]` keeps it safe (variants[0] → undefined).
          variants={[]}
          productProperties={reviewProductProperties}
          showReviewSummaryAndFilterTags={reviewSystemExp.showReviewSummaryAndFilterTags}
          totalReviewsCount={overallTotal}
          hasReviewGalleryPhotos
          onReviewsBottomVisible={handleReviewsBottomVisible}
        />
      </div>
    )
  }

  return (
    <div ref={setListTopRef}>
      <AllReviews
        reviews={reviews}
        reviewSystemExp={reviewSystemExp}
        totalReviewsCount={overallTotal}
        hasReviewGalleryPhotos
        productProperties={reviewProductProperties}
        // Empty array (PL has no product variants here): the shared sort Select reads `variants[0]?.productId`
        // for analytics — the optional chain guards the element, not the array, so an undefined `variants`
        // crashes on sort click. `[]` keeps it safe (variants[0] → undefined).
        variants={[]}
        rewardProgrammeLearnMoreUrl={rewardProgrammeLearnMoreUrl}
        showReviewsLoader={isLoading}
        onReviewImageClick={openReviewPopup}
        onReviewsBottomVisible={handleReviewsBottomVisible}
        filterProps={{
          sortByOptions,
          sortBy: sortById,
          setSortBy: handleSetSortBy,
          onSearchReview: () => undefined,
          setHasPhoto: () => undefined,
          hasPhoto: false,
          filterOptions,
          setFilterBy: handleSetFilterBy,
          filterBy,
          setPage: handleSetPage,
        }}
        paginationProps={{
          totalPages,
          page,
          onChange: handleSetPage,
          hideArrows: false,
          productProperties: reviewProductProperties,
          trackReviewPagination: true,
        }}
      />
    </div>
  )
}

// ---------------- REVIEWS_LIST (highlight cards) ----------------
const listCss = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'space-8',
  listStyle: 'none',
  padding: 0,
  margin: 0,
})

const itemCss = css({ display: 'flex', flexDirection: 'column', paddingY: 'space-12' })

function ReviewsListWidgetComponent(props: PLTypedWidgetProps<ReviewsListWidgetData>) {
  const variant = useReviewsVariant()
  const reviewSystemExp = useReviewSystemV3Experiment()
  const regionIdToDisplayName = useRegionIdToDisplayName()
  const localeConfig = useStatsigDynamicLocaleConfig()
  const rewardQuery = localeConfig.gladlyHelpCenter?.rewardProgrammeLearnMoreQuery
  const rewardProgrammeLearnMoreUrl = rewardQuery ? `/contact-us?${rewardQuery}` : undefined

  const items = props.data.reviewsListData?.items ?? []
  if (items.length === 0) return null

  // FULL surface: the legacy AllReviews list with live filter/sort/page re-fetch (own component so
  // its pagination hooks stay out of the shared mini path).
  if (variant === 'full') {
    return (
      <ReviewsListFullSurface
        widgetProps={props}
        reviewSystemExp={reviewSystemExp}
        regionIdToDisplayName={regionIdToDisplayName}
        rewardProgrammeLearnMoreUrl={rewardProgrammeLearnMoreUrl}
      />
    )
  }

  // MINI surface DS: condensed cards in a Panda `ul`, same content as the legacy card (no SCSS class).
  if (shouldUseDesignSystem()) {
    return (
      <ul className={listCss}>
        {items.map(review => (
          <li key={review.id} className={itemCss}>
            <ReviewHighlightCardDS
              rating={review.rating}
              title={review.title ?? ''}
              body={review.text ?? ''}
              brandName={brandNameFromTags(review)}
              reviewedInCountry={review.regionId ? regionIdToDisplayName[review.regionId] : undefined}
              isIncentivized={review.isIncentivized}
            />
          </li>
        ))}
      </ul>
    )
  }

  // MINI surface: the condensed highlight card. Fragment (no wrapper div) so the cards sit directly
  // under the container wrapper, matching the legacy highlights DOM; each carries the per-card class.
  return (
    <>
      {items.map(review => (
        <ProductReviewHighLight
          key={review.id}
          {...adaptReviewToHighlight(review, regionIdToDisplayName, rewardProgrammeLearnMoreUrl)}
          className={reviewHighlightStyles.container__wrapper__reviewHighLight}
        />
      ))}
    </>
  )
}

export const ReviewsListWidget: PLTypedWidgetInterface<ReviewsListWidgetData, WidgetType.REVIEWS_LIST> = {
  id: WidgetType.REVIEWS_LIST,
  errorBoundary: PLBaseErrorBoundary,
  render: ReviewsListWidgetComponent,
}
